/**
 * GoogleSheetsConfigModal — config UI for the `google_sheets` leg-node type.
 *
 * The client shares their Google Sheet with our shared service account (the
 * email is shown at the top), pastes the sheet URL, picks a tab, and maps Brain
 * fields to ordered spreadsheet columns. The server buffers each lead and
 * batch-appends rows under Google's API quota — see GoogleSheetsManager.
 *
 * No credentials are entered here: auth is a single shared service account held
 * server-side. Onboarding is just "share the sheet with this email."
 */

import React, { useEffect, useMemo, useState } from 'react';
import { getBrainFields, getGoogleSheetsServiceAccount } from '../../services/api';
import { BrainField } from '../../types';
import LegNodeStatsPanel from './LegNodeStatsPanel';
import { SYSTEM_FIELDS } from './systemFields';
import ClientAssignmentField from '../shared/ClientAssignmentField';

// A column is sourced from EITHER a Brain field OR a hardcoded static value.
interface ColumnRow { header: string; source: 'field' | 'static'; field: string; staticValue: string; required: boolean; }

interface Props {
  legNode: any;                     // LegNode; .config.googleSheets holds our config
  onSave: (config: any) => void;
  onClose: () => void;
  /** Called after the Client field writes an assignment. */
  onClientAssigned?: () => void;
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const card: React.CSSProperties = {
  background: '#fff', borderRadius: 10, width: 'min(720px, 96vw)',
  maxHeight: '92vh', overflowY: 'auto', padding: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
};
const label: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', margin: '12px 0 4px' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
const row2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const help: React.CSSProperties = { fontSize: 11, color: '#6b7280', marginTop: 3 };
const btnGhost: React.CSSProperties = { border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, padding: '2px 6px' };

/** Pull the long spreadsheet id out of a pasted URL (…/d/<ID>/edit) or accept a
 *  bare id. Returns '' when nothing usable is found. */
function parseSpreadsheetId(raw: string): string {
  const s = (raw || '').trim();
  if (!s) {return '';}
  const m = s.match(/\/d\/([a-zA-Z0-9-_]+)/);
  if (m) {return m[1];}
  if (/^[a-zA-Z0-9-_]{20,}$/.test(s)) {return s;}   // looks like a bare id
  return '';
}

const GoogleSheetsConfigModal: React.FC<Props> = ({ legNode, onSave, onClose, onClientAssigned }) => {
  const existing = legNode?.config?.googleSheets ?? {};
  const dataBodyId: string = legNode?.dataBodyId || legNode?.data_body_id || '';

  const [spreadsheetUrl, setSpreadsheetUrl] = useState<string>(
    existing.spreadsheetUrl || (existing.spreadsheetId ? `https://docs.google.com/spreadsheets/d/${existing.spreadsheetId}/edit` : '')
  );
  const [sheetName, setSheetName] = useState<string>(existing.sheetName || 'Sheet1');
  const [includeHeaderRow, setIncludeHeaderRow] = useState<boolean>(existing.includeHeaderRow !== false);
  const [columns, setColumns] = useState<ColumnRow[]>(
    Array.isArray(existing.columns) && existing.columns.length
      ? existing.columns.map((c: any) => {
          const isStatic = (c.static !== undefined && c.static !== null) && !c.field;
          return {
            header: c.header || c.field || '',
            source: (isStatic ? 'static' : 'field') as 'field' | 'static',
            field: c.field || '',
            staticValue: c.static ?? '',
            required: !!c.required,
          };
        })
      : []
  );

  const [brainFields, setBrainFields] = useState<BrainField[]>([]);
  const [saEmail, setSaEmail] = useState<string | null>(null);
  const [saConfigured, setSaConfigured] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (dataBodyId) {getBrainFields(dataBodyId).then(setBrainFields).catch(() => { /* leave empty */ });}
    getGoogleSheetsServiceAccount()
      .then(r => { setSaEmail(r.email); setSaConfigured(r.configured); })
      .catch(() => { /* endpoint optional */ });
  }, [dataBodyId]);

  const spreadsheetId = useMemo(() => parseSpreadsheetId(spreadsheetUrl), [spreadsheetUrl]);

  const addColumn = () => setColumns(cs => [...cs, { header: '', source: 'field', field: '', staticValue: '', required: false }]);
  const removeColumn = (i: number) => setColumns(cs => cs.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) => setColumns(cs => {
    const j = i + dir;
    if (j < 0 || j >= cs.length) {return cs;}
    const next = cs.slice();
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const setCol = (i: number, patch: Partial<ColumnRow>) =>
    setColumns(cs => cs.map((c, idx) => idx === i ? { ...c, ...patch } : c));

  const copyEmail = () => { if (saEmail) {navigator.clipboard?.writeText(saEmail).catch(() => {});} };

  const handleSave = () => {
    if (!spreadsheetId) { setError('Paste a valid Google Sheet URL (…/spreadsheets/d/<ID>/edit).'); return; }
    const cleanCols = columns
      .map(c => {
        if (c.source === 'static') {
          return { header: (c.header || '').trim(), static: c.staticValue };
        }
        return { header: (c.header || c.field).trim(), field: c.field.trim(), required: !!c.required };
      })
      // Keep static columns (even blank spacers) and any field column with a field picked.
      .filter((c: any) => c.static !== undefined || c.field);
    if (cleanCols.length === 0) { setError('Add at least one column (a Brain field or a static value).'); return; }

    const googleSheets: any = {
      spreadsheetId,
      spreadsheetUrl: spreadsheetUrl.trim() || undefined,
      sheetName: sheetName.trim() || 'Sheet1',
      columns: cleanCols,
      includeHeaderRow,
    };
    onSave({ ...(legNode.config || {}), googleSheets });
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Google Sheets</h2>
          {legNode?.id && (
            <LegNodeStatsPanel dataBodyId={dataBodyId} nodeId={legNode.id} brainFields={brainFields} />
          )}
        </div>
        <p style={{ ...help, marginTop: 4 }}>
          Appends each lead as a row in a Google Sheet. Rows are batched under Google’s API quota, so it never rate-limits and never drops a lead.
        </p>

        {/* Client — deliberately the first thing in the modal. Who a
            destination delivers to is the only decision in here that
            cannot be undone once a lead has gone out. Self-contained:
            it fetches and writes the assignment itself rather than
            riding on this modal's own save flow. */}
        <ClientAssignmentField
          dataBodyId={legNode.dataBodyId}
          legNodeId={legNode.id}
          nodeType={legNode.type}
          onAssigned={onClientAssigned}
        />

        {/* Share-with step — the whole onboarding for the client. */}
        <div style={{ marginTop: 12, padding: 12, background: saConfigured ? '#f0f9ff' : '#fef2f2', border: `1px solid ${saConfigured ? '#bae6fd' : '#fecaca'}`, borderRadius: 8 }}>
          {saConfigured ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#0369a1' }}>Step 1 — share the sheet</div>
              <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                In Google Sheets, click <strong>Share</strong> and give <strong>Editor</strong> access to:
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                <code style={{ flex: 1, fontSize: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', wordBreak: 'break-all' }}>
                  {saEmail || '—'}
                </code>
                <button type="button" onClick={copyEmail} disabled={!saEmail} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12 }}>Copy</button>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: '#b91c1c' }}>
              Google Sheets delivery isn’t set up on the server yet (no service account key). Ask an admin to set <code>GOOGLE_SHEETS_SA_KEY</code>, then reopen this dialog to see the share-with email.
            </div>
          )}
        </div>

        <label style={label}>Step 2 — Google Sheet URL</label>
        <input style={input} value={spreadsheetUrl} onChange={e => setSpreadsheetUrl(e.target.value)}
          placeholder="https://docs.google.com/spreadsheets/d/1AbC…/edit" />
        <div style={help}>{spreadsheetId ? `Sheet id: ${spreadsheetId}` : 'Paste the full URL from your browser’s address bar.'}</div>

        <div style={row2}>
          <div>
            <label style={label}>Tab name</label>
            <input style={input} value={sheetName} onChange={e => setSheetName(e.target.value)} placeholder="Sheet1" />
            <div style={help}>The tab (bottom of the sheet) rows are appended to.</div>
          </div>
          <div>
            <label style={label}>Header row</label>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <input type="checkbox" checked={includeHeaderRow} onChange={e => setIncludeHeaderRow(e.target.checked)} />
              Write a header row if the tab is empty
            </label>
          </div>
        </div>

        <label style={label}>Step 3 — columns (in order, left → right)</label>
        {columns.length === 0 && <div style={help}>No columns yet — add one below. Each column is either a Brain field or a hardcoded static value.</div>}
        {columns.map((c, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button type="button" style={btnGhost} onClick={() => move(i, -1)} disabled={i === 0} title="Move up">↑</button>
              <button type="button" style={btnGhost} onClick={() => move(i, 1)} disabled={i === columns.length - 1} title="Move down">↓</button>
            </div>
            <input
              style={{ ...input, flex: 1 }}
              value={c.header}
              onChange={e => setCol(i, { header: e.target.value })}
              placeholder="Column heading"
            />
            <span style={{ color: '#9ca3af' }}>←</span>
            <select
              style={{ ...input, width: 110, flex: 'none' }}
              value={c.source}
              onChange={e => setCol(i, { source: e.target.value as 'field' | 'static' })}
              title="Where this column's value comes from"
            >
              <option value="field">Brain field</option>
              <option value="static">Static value</option>
            </select>
            {c.source === 'static' ? (
              <input
                style={{ ...input, flex: 1 }}
                value={c.staticValue}
                onChange={e => setCol(i, { staticValue: e.target.value })}
                placeholder="Hardcoded value (e.g. abc-123-xyz)"
              />
            ) : (
              <select
                style={{ ...input, flex: 1 }}
                value={c.field}
                onChange={e => setCol(i, { field: e.target.value, header: c.header || e.target.value })}
              >
                <option value="">{brainFields.length === 0 ? '— Pick a field —' : '— Pick a Brain field —'}</option>
                {brainFields.map(b => <option key={b.fieldName} value={b.fieldName}>{b.fieldName}</option>)}
                <optgroup label="System Fields">
                  {SYSTEM_FIELDS.map(s => <option key={s.id} value={s.id} title={`e.g. ${s.example}`}>{s.label} — {s.example}</option>)}
                </optgroup>
              </select>
            )}
            {c.source === 'field' ? (
              <label style={{ fontSize: 12, whiteSpace: 'nowrap' }} title="Drop the lead (logged) if this field is empty">
                <input type="checkbox" checked={c.required} onChange={e => setCol(i, { required: e.target.checked })} /> req
              </label>
            ) : (
              <span style={{ width: 34 }} />
            )}
            <button type="button" onClick={() => removeColumn(i)} style={{ ...btnGhost, color: '#ef4444' }} title="Remove">✕</button>
          </div>
        ))}
        <button type="button" onClick={addColumn} style={{ marginTop: 4, padding: '6px 12px', border: '1px dashed #9ca3af', borderRadius: 6, background: '#f9fafb', cursor: 'pointer', fontSize: 13 }}>
          + Add column
        </button>
        <div style={help}>Each column is a <strong>Brain field</strong> (per-lead value) or a <strong>Static value</strong> (same hardcoded value on every row — e.g. a list_id). Order = left→right in the sheet. “req” drops leads missing that Brain field (logged as failed, never silently). Header text is what appears in the sheet’s top row (empty tab only). Tip: a Static value left blank makes a spacer column to line up with a client’s existing layout.</div>

        {error && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button type="button" onClick={onClose} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
          <button type="button" onClick={handleSave} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer' }}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default GoogleSheetsConfigModal;
